import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, FileText, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ScrollArea } from '../ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'wouter';
import { apiRequest } from '../../lib/queryClient';

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  related_id: number | null;
  related_type: string | null;
  is_read: boolean;
  created_at: string;
}

export function NotificationsDropdown() {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  // Fetch notifications
  const { data: notifications = [], refetch } = useQuery<Notification[]>({
    queryKey: ['/api/protected/notifications'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/protected/notifications');
      return response.json();
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest('PUT', '/api/protected/notifications', { id, isRead: true });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/protected/notifications'] });
    },
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'estimate_signed':
        return <FileText className="h-4 w-4 text-green-500" />;
      case 'event_rescheduled':
      case 'event_cancelled':
        return <Calendar className="h-4 w-4 text-blue-500" />;
      default:
        return <Bell className="h-4 w-4 text-slate-500" />;
    }
  };

  const getNotificationLink = (notification: Notification) => {
    if (notification.related_type === 'estimate' && notification.related_id) {
      // For signed estimates, send users to a read-only view (print page),
      // not to the editable estimate form.
      if (notification.type === 'estimate_signed') {
        return `/estimates/${notification.related_id}/print`;
      }
      // Other estimate-related notifications can still go to the edit form
      return `/estimates/edit/${notification.related_id}`;
    }
    if (notification.related_type === 'event' && notification.related_id) {
      return `/calendar`;
    }
    return null;
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsReadMutation.mutate(notification.id);
    }
    setIsOpen(false);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="remodra-topnav-icon h-9 w-9 sm:h-9 sm:w-9 md:h-10 md:w-10 relative flex-shrink-0"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4 sm:h-4 sm:w-4 md:h-5 md:w-5" />
          {unreadCount > 0 && (
            <span className="absolute top-0.5 right-0.5 w-2 h-2 sm:w-2.5 sm:h-2.5 bg-green-500 rounded-full border-2 border-white dark:border-slate-800 animate-pulse"></span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="w-80 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" 
        align="end"
        sideOffset={5}
      >
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <Badge className="bg-green-500 text-white">{unreadCount} new</Badge>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ScrollArea className="h-[400px]">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-500">
              No notifications
            </div>
          ) : (
            <div className="py-1">
              {notifications.map((notification) => {
                const link = getNotificationLink(notification);
                const handleClick = (e: React.MouseEvent) => {
                  // Always mark as read when clicked
                  if (!notification.is_read) {
                    markAsReadMutation.mutate(notification.id);
                  }
                  // Close dropdown
                  setIsOpen(false);
                  // Navigate if there's a link
                  if (link) {
                    // Use wouter's navigate programmatically
                    window.location.href = link;
                  }
                };

                const NotificationContent = (
                  <div
                    className={`flex items-start gap-3 p-3 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer ${
                      !notification.is_read ? 'bg-green-50/50 dark:bg-green-900/10' : ''
                    }`}
                    onClick={handleClick}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${!notification.is_read ? 'text-slate-900 dark:text-slate-100' : 'text-slate-700 dark:text-slate-300'}`}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                        {notification.message}
                      </p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    {!notification.is_read && (
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      </div>
                    )}
                  </div>
                );

                // Always render as div, handle navigation in onClick
                return (
                  <div key={notification.id}>
                    {NotificationContent}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="text-center justify-center text-sm text-slate-600 dark:text-slate-400"
              onClick={() => {
                // Mark all as read
                notifications.forEach(n => {
                  if (!n.is_read) {
                    markAsReadMutation.mutate(n.id);
                  }
                });
              }}
            >
              Mark all as read
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

